import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  CheckCircle2,
  Target,
  Building2,
  TrendingUp,
  Factory,
} from "lucide-react";

export default function Manufacturers() {
  const location = useLocation();
  useEffect(() => {
    document.title = "Why Manufacturers Choose LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute("content", "LumberLoop USA generates qualified contractor demand for manufacturers and drives new business to your existing dealer network.");
  }, []);

  // Smooth scroll to hash anchor when nav dropdowns link here
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return (
    <div data-testid="manufacturers-page">
      {/* HERO */}
      <section className="relative bg-[#0F2540] text-white overflow-hidden border-b-2 border-[#1A365D]">
        <div className="stripes-stars absolute inset-0 opacity-[0.105]" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-tag text-slate-300">For Manufacturers</div>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-black uppercase mt-3 leading-[0.95] !text-white">
            We Don&apos;t Sell Advertising. <span className="text-[#C62828]">We Create Demand.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-200 leading-relaxed max-w-3xl">
            Contractors need a solution that allows them to find your product locally, quickly, and get the information they need to quote, sell, and install your products. LumberLoop USA allows you to be that solution — we bridge the gap between you and the contractor by seamlessly providing them the information they need when they need it. <span className="text-white font-semibold">You gain credibility, contractor data, dealer accountability, and more sales.</span>
          </p>
        </div>
        <div className="h-2 stripes-flag" aria-hidden="true" />
      </section>

      {/* INTRO NARRATIVE */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16 space-y-5 text-lg text-slate-700 leading-relaxed">
        <p>Every day, millions of potential customers visit manufacturer websites — and most companies can only track <em>who</em> visited with analytics software. Once the potential customer leaves your page… where does he or she go?</p>
        <p>Our platform doesn&apos;t just capture the visit — we direct that buyer to the dealers who help <strong>demonstrate, educate, and sell</strong> your products. <span className="text-[#C62828] font-semibold">Automatically.</span></p>
        <p className="font-heading text-2xl md:text-3xl font-bold text-[#1A365D] uppercase leading-snug border-l-4 border-[#C62828] pl-5">
          When opportunity meets solutions… everybody wins.
        </p>
      </section>

      {/* WHY JOIN — ROI */}
      <section id="why-join" className="bg-[#EAEAE5] border-y-2 border-[#1A365D] py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="label-tag">Why Join?</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mt-2 leading-tight text-[#1A365D]">
            Why Manufacturers Choose <span className="text-[#C62828]">LumberLoop USA.</span>
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            <Tier label="Advertising" body="Creates impressions." accent="bg-slate-400"/>
            <Tier label="Marketing" body="Creates clicks." accent="bg-slate-600"/>
            <Tier label="LumberLoop USA" body="Creates buying opportunities." accent="bg-[#C62828]" hero/>
          </div>
          <p className="mt-8 text-lg text-slate-700 leading-relaxed">
            Our goal is to allow your customers to get a quick quote on your products, provide your existing dealers with qualified leads, and build your relationship throughout the channel. Every lead has the potential to become <strong>years of recurring sales</strong> — and we don&apos;t want you to miss out on this opportunity.
          </p>
        </div>
      </section>

      {/* DEALER NETWORK */}
      <section className="bg-white border-y-2 border-[#1A365D] py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="label-tag">Built To Support Your Channel</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mt-2 leading-tight text-[#1A365D]">
            We strengthen your <span className="text-[#C62828]">dealer network.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-700 leading-relaxed">
            Unlike online marketplaces that <strong>compete</strong> with dealers, LumberLoop USA was built to <strong>support</strong> them. Our mission is to send qualified buyers directly to the dealers already selling your products.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { icon: Building2, title: "Dealers stock more inventory" },
              { icon: TrendingUp, title: "Distributors reorder more product" },
              { icon: Factory, title: "Manufacturers grow sales" },
            ].map((it) => (
              <div key={it.title} className="brutal-card-flat p-5">
                <div className="w-10 h-10 bg-[#0F2540] text-white flex items-center justify-center"><it.icon size={18}/></div>
                <div className="font-heading font-bold uppercase mt-3 text-[#1A365D] leading-tight">{it.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY MANUFACTURERS JOIN */}
      <section className="bg-[#EAEAE5] border-y-2 border-[#1A365D] py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="label-tag">Why Manufacturers Join</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mt-2 leading-tight text-[#1A365D]">
            Because demand <span className="text-[#C62828]">drives sales.</span>
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {[
              "Generate qualified contractor leads",
              "Increase product awareness",
              "Support their dealer network",
              "Strengthen distributor relationships",
              "Introduce new products faster",
              "Create pull-through demand",
              "Expand into new markets",
              "Increase long-term product specifications",
            ].map((it) => (
              <div key={it} className="flex items-start gap-2 text-slate-700">
                <CheckCircle2 size={18} className="text-[#C62828] mt-0.5 flex-shrink-0"/>
                <span>{it}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUILT BY INDUSTRY PROFESSIONALS */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="label-tag">Built By Industry Professionals</div>
        <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mt-2 leading-tight text-[#1A365D]">
          Built by people who have <span className="text-[#C62828]">walked the job sites.</span>
        </h2>
        <div className="mt-6 space-y-4 text-lg text-slate-700 leading-relaxed">
          <p>LumberLoop USA wasn&apos;t created by software developers trying to understand construction. It was created by professionals who have spent decades selling building products through lumberyards, wholesale distributors, architects, builders, and contractors.</p>
          <p>We&apos;ve walked job sites. We&apos;ve worked dealer counters. We&apos;ve built manufacturer sales territories. We&apos;ve trained contractors. We&apos;ve launched products. We&apos;ve grown brands.</p>
          <p className="font-heading text-xl md:text-2xl font-bold text-[#1A365D] uppercase border-l-4 border-[#C62828] pl-5">
            We understand how building products are bought — because we&apos;ve spent our careers helping manufacturers sell them.
          </p>
        </div>
      </section>
    </div>
  );
}

function Tier({ label, body, accent, hero = false }) {
  return (
    <div className={`brutal-card-flat p-5 ${hero ? "!bg-[#C62828] !border-[#C62828] text-white" : "bg-white"}`}>
      <div className={`w-10 h-10 ${accent} text-white flex items-center justify-center`}>
        <Target size={18}/>
      </div>
      <div className={`font-heading text-xl font-bold uppercase mt-3 ${hero ? "text-white" : "text-[#1A365D]"}`}>{label}</div>
      <div className={`text-sm mt-1 ${hero ? "text-white/90" : "text-slate-700"}`}>{body}</div>
    </div>
  );
}
